import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Hint } from "./Hint";
import { readPromptExclusions } from "./promptExclusions";

export default function ForbiddenTagsEditor({
  prompt,
  words,
  disabled,
  onEdit,
}: {
  prompt: string;
  words: string[];
  disabled: boolean;
  onEdit: (tag: string, action: "add" | "remove") => boolean | Promise<boolean>;
}) {
  const [value, setValue] = useState("");
  const [search, setSearch] = useState("");
  const state = useMemo(() => {
    try {
      const { rulesText } = readPromptExclusions(prompt);
      return {
        tags: words,
        rulesText,
        error: "",
      };
    } catch (error) {
      return {
        tags: words,
        rulesText: "",
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }, [prompt, words]);
  const locked = disabled;
  const add = async () => {
    if (!locked && value.trim() && (await onEdit(value, "add"))) setValue("");
  };
  const visibleTags = state.tags.filter((tag) =>
    tag.toLowerCase().includes(search.trim().toLowerCase()),
  );
  return (
    <section
      aria-label="禁止标签列表"
      className="flex shrink-0 items-center justify-between gap-2 rounded-lg border px-3 py-2"
    >
      <span className="text-sm font-medium">
        全局禁止标签：{words.length} 个
      </span>
      <Dialog
        onOpenChange={() => {
          setSearch("");
          setValue("");
        }}
      >
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-7 px-1 text-blue-600"
          >
            管理
          </Button>
        </DialogTrigger>
        <DialogContent className="flex max-h-[80vh] flex-col overflow-hidden sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>管理禁止标签（{words.length} 个）</DialogTitle>
            <DialogDescription>
              添加或删除后全局生效，所有运营共用，不受草稿或提示词版本影响。
            </DialogDescription>
          </DialogHeader>
          <div className="flex shrink-0 gap-2">
            <Input
              id="forbidden-tag-input"
              aria-label="添加禁止标签"
              value={value}
              disabled={locked}
              maxLength={23}
              placeholder="例如 ghost，回车添加"
              className="h-8 min-w-0 text-xs"
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  add();
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8"
              disabled={locked || !value.trim()}
              onClick={add}
            >
              添加
            </Button>
          </div>
          {state.error ? (
            <p role="alert" className="text-xs text-destructive">
              {state.error}
            </p>
          ) : null}
          <Input
            aria-label="搜索禁止标签"
            placeholder="搜索已禁止的标签"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-9 shrink-0"
          />
          {visibleTags.length ? (
            <ul
              aria-label="已禁止的标签"
              className="flex min-h-0 max-h-72 flex-wrap content-start gap-1.5 overflow-y-auto"
            >
              {visibleTags.map((tag) => (
                <li
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs"
                >
                  <span>{tag}</span>
                  <Hint content={`取消禁止 ${tag}`}>
                    <button
                      type="button"
                      disabled={locked}
                      aria-label={`移除禁止标签 ${tag}`}
                      onClick={() => onEdit(tag, "remove")}
                      className="rounded p-0.5 hover:bg-background focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Hint>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">
              {state.tags.length
                ? "没有匹配的禁止标签"
                : "暂无禁止标签，在上方输入标签即可添加。"}
            </p>
          )}
          {state.rulesText ? (
            <p className="text-xs text-muted-foreground">
              草稿中已有的其他禁止规则继续生效，可在“本地草稿”中编辑。
            </p>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
